import { HttpExecutionContext, HttpResponse, type HttpPlugin } from "@kalodata/http";
import { applyLegacyRequestHeaders } from "./applyLegacyRequestHeaders";
import { applyLegacyRequestQuery } from "./applyLegacyRequestQuery";
import { reportHttpRequestSuccess } from "./reportHttpEvent";
import { resolveTraceIdFromHeaders } from "./traceId";
import type { ClipHttpContext, ClipHttpEnvelope, ClipHttpMeta } from "./types";
import { LOCALSTORAGE_KEY } from "@config";
import { message } from "antd";
import { ClipErrorCode, getErrorMessageWithOptions } from "@utils/errrorMessage";
import { t } from "i18next";
import { copyTextToClipboard } from "@kalodata/tools";
import { markHttpErrorToastShown, isHttpErrorToastShown } from "./httpErrorToast";

/** 接口的默认超时时间，1分钟 */
export const DEFAULT_CLIP_TIMEOUT_MS = 60 * 1000;

export const isBusinessError = (response: HttpResponse<unknown, ClipHttpMeta, ClipHttpContext>) => {
  const responseData = response.data as ClipHttpEnvelope;
  return !!responseData && responseData.success === false;
};

export const createHttpRequestReportSuccessPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "httpRequestLifecycleReport",
    onResponse: (response, context) => {
      if (response.status === 200) {
        reportHttpRequestSuccess(context.request, resolveTraceIdFromHeaders(response.headers));
      }
      return response;
    },
  };
};

export const createLegacyHeadersPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "legacyHeaders",
    onRequest: (request) => applyLegacyRequestHeaders(request),
  };
};

export const createLegacyQueryPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "legacyQuery",
    onRequest: (request) => applyLegacyRequestQuery(request),
  };
};

export const createAttachAccessTokenPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "attachAccessToken",
    onRequest: (request) => {
      const accessToken = localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);
      if (!accessToken) return request;
      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
    },
  };
};

/**
 * 旧 axios 链路对 HTTP `410` / `411` 有专门处理：
 * - `410`：以 `message.warning` 弹账号锁定文案。
 * - `411`：通过 PubSub 触发全局验证码弹窗（`handleStatus411`）。
 *
 * 怀疑是历史遗留逻辑，迁移阶段先占位插件，避免遗忘；如需恢复行为，请在 onError 内补对应分支。
 *
 * TODO: 与产品 / 后端确认 410、411 是否仍在使用，再决定是否实现具体兜底。
 */
export const createLegacyStatusFallbackPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "legacyStatusFallback",
    onError: (error) => {
      // TODO: 410 -> 账号锁定 warning；411 -> 触发全局验证码。当前未实现，仅占位。
      throw error;
    },
  };
};

export const createToastErrorPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "toastError",
    onError: (error, exe_context: HttpExecutionContext<ClipHttpMeta, ClipHttpContext>) => {
      const {
        request: { context },
      } = exe_context;
      // 上游插件（如 auth 跳转登录）已处理并标记的错误不再重复弹 toast。
      if (!context?.silent && !isHttpErrorToastShown(error)) {
        const { code, message: errorMessage, status } = error;
        const responseData = (error as { response?: { data?: ClipHttpEnvelope } }).response?.data;
        const effectiveCode = code ?? responseData?.code;
        const codeMsg = effectiveCode
          ? getErrorMessageWithOptions(effectiveCode as unknown as ClipErrorCode, error)
          : null;
        const finalMsg =
          codeMsg?.message ||
          responseData?.message ||
          errorMessage ||
          `Request failed: ${effectiveCode || status || "Unknown error"}`;
        const finalMsgOptions = codeMsg?.options;
        const traceId = error.traceId || resolveTraceIdFromHeaders(error.response?.headers);
        const copyTraceId = () => {
          if (!traceId) return;
          copyTextToClipboard(traceId)
            .then(() => {
              message.success(t("Copied"));
            })
            .catch(() => {});
        };

        message.error({
          content: (
            <span className="inline-flex flex-col">
              <span>{t(finalMsg, finalMsgOptions)}</span>
              {traceId && (
                <span className="select-all cursor-pointer" title={t("Click to copy")} onClick={copyTraceId}>
                  {`Trace ID: ${traceId}`}
                </span>
              )}
            </span>
          ),
          duration: context?.toastDuration ?? 5,
        });
        markHttpErrorToastShown(error);
      }
      throw error;
    },
  };
};
